/**
 * Appointment Data Helpers
 *
 * Pure utilities for extracting appointment data from context.
 * NOTE: For guards (boolean checks), use guards/context.guard.ts instead.
 */

import type { KioskMachineContext } from "../types.js";

/**
 * Shape of appointment data in context
 */
export interface AppointmentData {
  boughtTreatmentWithCharges?: Array<{
    id: string;
    hrtId: number;
    [key: string]: unknown;
  }>;
  slot?: { id: string; [key: string]: unknown };
  appointment?: { id: string; [key: string]: unknown };
}

/**
 * Result of extracting appointment data
 */
export interface ExtractedAppointmentData {
  exists: boolean;
  appointmentId: string | undefined;
  slotId: string | undefined;
  boughtTreatmentIds: string[];
  existingHrtId: number | undefined;
}

/**
 * Extracts appointment data from context with proper typing.
 * Centralizes the repeated pattern of casting context.appointment.
 *
 * Use this when you need to extract multiple pieces of appointment data.
 * For simple boolean checks, use guards instead (hasExistingAppointment, isTreatmentChanged).
 */
export function extractAppointmentData(
  context: KioskMachineContext,
): ExtractedAppointmentData {
  const appointment = context.appointment as
    | { data?: AppointmentData }
    | undefined;

  const boughtTreatments = appointment?.data?.boughtTreatmentWithCharges ?? [];
  const exists = boughtTreatments.length > 0;

  return {
    exists,
    appointmentId: appointment?.data?.appointment?.id,
    slotId: appointment?.data?.slot?.id,
    boughtTreatmentIds: boughtTreatments.map((bt) => bt.id),
    existingHrtId: boughtTreatments[0]?.hrtId,
  };
}

/**
 * Checks if treatment changed based on event data.
 *
 * This is NOT a guard - it's a utility for inline guard functions that need
 * to compare event.data.hrtIds (not yet in context) with existing appointment.
 *
 * For context-only checks, use the isTreatmentChanged guard instead.
 */
export function isTreatmentChangedFromEvent(
  context: KioskMachineContext,
  eventData: { hrtIds?: number[] } | undefined,
): boolean {
  const { exists, existingHrtId } = extractAppointmentData(context);

  if (!exists || existingHrtId == null) {
    return false;
  }

  const newHrtId = eventData?.hrtIds?.[0];
  if (newHrtId == null) {
    return false;
  }

  return existingHrtId !== newHrtId;
}
